<template>
  <div class="employee-discount-card" data-testid="payroll-shared-employee-discount-card">
    <div class="card-header">
      <div class="employee-info">
        <i class="pi pi-user employee-icon"></i>
        <div class="employee-details">
          <h4 class="employee-name">{{ employee.employee_name }}</h4>
          <p v-if="employeeCount > 1" class="employee-count">
            {{ t('{count} {item}', { count: employeeCount, item: itemTypeLabel }) }}
          </p>
        </div>
      </div>
      <div class="card-summary">
        <span class="summary-label">{{ t('Total selecionado') }}</span>
        <span class="summary-value">{{ n(selectedTotal, 'currency') }}</span>
      </div>
    </div>

    <div class="card-body">
      <div v-if="items.length === 0" class="empty-state">
        <i class="pi pi-check-circle"></i>
        <p>{{ t('Nenhum item pendente para este colaborador') }}</p>
      </div>

      <div v-else class="items-list">
        <div
          v-for="item in items"
          :key="item.id"
          class="discount-item"
          :class="{ 'is-selected': isSelected(item.id) }"
        >
          <div class="item-header">
            <div class="item-info">
              <Toggle
                :modelValue="isSelected(item.id)"
                :data-testid="`payroll-shared-discount-toggle-${item.id}`"
                @update:modelValue="handleToggle(item.id, $event)"
              />
              <div class="item-details">
                <div class="item-title-row">
                  <span class="item-title">{{ item.title }}</span>
                  <Tag
                    v-if="item.source_type"
                    :value="getSourceTypeLabel(item.source_type)"
                    :severity="getSourceTypeSeverity(item.source_type)"
                    :icon="getSourceTypeIcon(item.source_type)"
                    class="source-type-badge"
                  />
                </div>
                <span class="item-meta">{{ formatDateStr(item.request_date || item.created_at) }}</span>
              </div>
            </div>

            <div class="item-amounts">
              <div class="amount-row">
                <span class="amount-label">{{ t('Total') }}:</span>
                <span class="amount-value">{{ n(item.amount, 'currency') }}</span>
              </div>
              <div class="amount-row">
                <span class="amount-label">{{ t('Restante') }}:</span>
                <span class="amount-value amount-remaining">{{ n(item.amount_remaining, 'currency') }}</span>
              </div>
            </div>
          </div>

          <div v-if="isSelected(item.id)" class="item-discount-input">
            <label>{{ t('Valor a descontar') }}:</label>
            <div class="input-wrapper">
              <InputCurrency
                v-model="selectedDiscounts[item.id]"
                :min="0"
                :max="item.amount_remaining"
                elem-id="discount-amount"
                :label="''"
                :placeholder="t('0,00')"
                :data-testid="`payroll-shared-discount-amount-${item.id}`"
                @update:modelValue="handleAmountChange(item.id, $event)"
              />
              <span class="remaining-hint">
                {{ t('Máximo: {max}', { max: n(item.amount_remaining, 'currency') }) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import InputCurrency from '../../../../inputs/InputCurrency.vue';
import { useDateFormatter } from '../../../../composables/useDateFormatter';
import { eventBus } from '../../../../eventBus';
import { ADVANCE_SOURCE_TYPE, ADVANCE_SOURCE_TYPE_LABELS, ADVANCE_SOURCE_TYPE_SEVERITY } from '../../../../composables/useEmployeeAdvances';

type Id = number | string;

interface DiscountItem {
  id: Id;
  title: string;
  amount: number;
  amount_remaining: number;
  source_type?: string;
  request_date?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface Employee {
  employee_id: Id;
  employee_name: string;
  [key: string]: unknown;
}

interface DiscountPayload {
  itemId: Id;
  amount: number | null;
}

interface Props {
  employee: Employee;
  items: DiscountItem[];
  selectedDiscounts?: Record<Id, number>;
}

const props = withDefaults(defineProps<Props>(), {
  selectedDiscounts: () => ({}),
});

const emit = defineEmits<{
  'update:discount': [payload: DiscountPayload];
}>();

const { t, n } = useI18n();
const { formatDate } = useDateFormatter();

const formatDateStr = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return formatDate(new Date(dateStr));
};

const employeeCount = computed((): number => props.items.length);

const itemTypeLabel = computed((): string => {
  if (props.items.length > 0) {
    const firstItem = props.items[0];
    if ('request_date' in firstItem) {
      return t('adiantamento');
    }
  }
  return t('dedução');
});

const selectedTotal = computed((): number => {
  return Object.values(props.selectedDiscounts).reduce(
    (sum: number, amount: number) => sum + (parseFloat(String(amount)) || 0),
    0
  );
});

const isSelected = (itemId: Id): boolean => {
  return itemId in props.selectedDiscounts;
};

const handleToggle = (itemId: Id, checked: boolean): void => {
  const item = props.items.find(i => i.id === itemId);
  if (!item) return;

  if (checked) {
    emit('update:discount', { itemId, amount: item.amount_remaining });
  } else {
    emit('update:discount', { itemId, amount: null });
  }
};

const handleAmountChange = (itemId: Id, amount: number): void => {
  const item = props.items.find(i => i.id === itemId);
  if (!item) return;

  const parsedAmount = parseFloat(String(amount)) || 0;

  if (parsedAmount > item.amount_remaining) {
    eventBus.emit('globalMessage.warning', {
      message: t('O valor a descontar não pode ser maior que o valor restante do {item_type}. Máximo: {max}', {
        item_type: itemTypeLabel.value,
        max: n(item.amount_remaining, 'currency')
      }),
      duration: 5000
    });
    return;
  }

  emit('update:discount', { itemId, amount: parsedAmount });
};

const getSourceTypeLabel = (sourceType: string): string => {
  return (ADVANCE_SOURCE_TYPE_LABELS as Record<string, string>)[sourceType] || sourceType;
};

const getSourceTypeSeverity = (sourceType: string): string => {
  return (ADVANCE_SOURCE_TYPE_SEVERITY as Record<string, string>)[sourceType] || 'info';
};

const getSourceTypeIcon = (sourceType: string): string => {
  switch (sourceType) {
    case ADVANCE_SOURCE_TYPE.CASH_RECEBIMENTO:
      return 'pi pi-money-bill';
    case ADVANCE_SOURCE_TYPE.MANUAL:
    default:
      return 'pi pi-pencil';
  }
};
</script>

<style scoped>
.employee-discount-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1rem;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.employee-icon {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.employee-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.employee-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.employee-count {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.card-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.summary-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary-color);
}

.card-body {
  padding: 1.5rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--text-color-secondary);
  text-align: center;
}

.empty-state i {
  font-size: 2rem;
  color: var(--green-500);
  margin-bottom: 0.5rem;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.discount-item {
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.discount-item.is-selected {
  border-color: var(--primary-color);
  background: var(--primary-50);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.item-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.item-title {
  font-weight: 600;
  color: var(--text-color);
}

.item-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.source-type-badge {
  font-size: 0.75rem;
}

.item-meta {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.item-amounts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.amount-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.amount-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.amount-value {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-color);
}

.amount-remaining {
  color: var(--orange-600);
}

.item-discount-input {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
}

.item-discount-input label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.5rem;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remaining-hint {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}
</style>
